import { buildAvailabilityWriteWarning } from '../../../utils/commitment-reprice-warnings';
import { BatchUpsertAvailabilitiesCompactResult } from '../../court-management/hooks/useBatchUpsertAvailabilitiesCompact';
import { buildUnchargeableCapacityWarning } from './unchargeable-capacity-warning';

/**
 * Everything an availability save can do silently, as ONE sentence.
 *
 * The save answers four independent questions, and all four used to be
 * reachable only by a player failing to book — or, for the last two, by nobody
 * at all:
 *
 *  - `unchargeableCapacityPrices` — a price nobody can PAY. `price / capacity`
 *    lands in Stripe's 1–49 cent dead zone and every booking at that size is
 *    refused (D25).
 *  - `capacityPricingWarnings` — a size nobody can BUY. Replacing a window's
 *    rows with a narrower set of configs takes the venue's last price for a
 *    capacity off sale, while the capacity tab keeps offering it and the admin
 *    booking modal keeps letting an operator pick it (D136). The server's
 *    commitment guard cannot see this: it diffs bookings that ALREADY exist, so
 *    with nothing yet booked at that size the save commits cleanly.
 *  - `repricedCommitments` — a price that moved UNDER bookings already sold.
 *    The commitment guard does see these (it refuses the save if they would
 *    lose their price outright at their capacity), and said nothing at all
 *    about the case where they keep it at a new amount.
 *  - `unpricedCommitments` — a booking already sold that this save left with NO
 *    price at all, because the slot stopped selling entirely (D165). The guard
 *    sees these too and deliberately does not refuse them — narrowing a court's
 *    hours over a booked game is a routine edit and blocking it platform-wide
 *    would be worse — but until D165 it only wrote them to a log line nothing
 *    retains.
 *
 * Joined rather than shown as separate toasts, on the rule the option-side
 * warnings already follow: a second toast lands on top of the first and is read
 * as a duplicate. The unchargeable-price sentence leads because it is the one
 * the operator fixes with the smallest edit — re-typing a price, against
 * re-pricing a capacity across the schedule. The other three come from
 * `buildAvailabilityWriteWarning`, which owns their order (and orders them by
 * what it costs to ignore) so this rail and the delete rail cannot drift apart
 * on it.
 *
 * Returns `null` when there is nothing to say, so the caller keeps its plain
 * success toast. A warning that fires on an ordinary save is one the operator
 * learns to dismiss, which is exactly how the one that matters gets missed.
 */
export function buildAvailabilitySaveWarning(
  result: BatchUpsertAvailabilitiesCompactResult | undefined | null,
): string | null {
  const sentences = [
    buildUnchargeableCapacityWarning(result?.unchargeableCapacityPrices),
    buildAvailabilityWriteWarning(result),
  ].filter((sentence): sentence is string => Boolean(sentence));

  return sentences.length > 0 ? sentences.join(' ') : null;
}
